import React from 'react'
import { Pressable, Text, View, type ViewProps } from 'react-native'
import { useTranslation } from 'react-i18next'

import { cn } from '@/utils/cn'
import { copyToClipboard } from '@/utils/copy'
import { useToast } from '@/components/base'
import { ChevronRight, Copy, QrCode } from '@/components/icon'

type DescriptionListItemProps = {
  isValuePrimary?: boolean
  label: string
  description?: string
  children: string
  hasBorderTop?: boolean
  hasBorderBottom?: boolean
  inset?: boolean
  canCopy?: boolean
  onPress?: () => void
  onOpenQrCode?: () => void
}

function DescriptionListItem({
  isValuePrimary = false,
  label,
  description,
  children,
  canCopy = false,
  onPress,
  onOpenQrCode,
  ...props
}: DescriptionListItemProps) {
  const { t } = useTranslation()
  const toast = useToast()

  return (
    <Pressable className={'bg-background-primary'} onPress={onPress || onOpenQrCode}>
      {({ pressed }) => (
        <View className={cn((onPress || onOpenQrCode) && pressed && 'bg-background-secondary')}>
          <ListItemView {...props}>
            <Text className={'text-sm text-text-secondary'}>{label}</Text>
            <View className={'flex-row gap-2'}>
              <View className={'items-end'}>
                <Text
                  className={cn(
                    'max-w-[202px] text-right font-roboto-medium text-sm font-medium text-text-primary',
                    isValuePrimary && 'text-primary'
                  )}
                >
                  {children}
                </Text>
                {description && <Text className={'text-xs text-text-secondary'}>{description}</Text>}
              </View>
              {canCopy && (
                <Pressable
                  className={'m-[-16px] p-4'}
                  onPress={() => {
                    copyToClipboard(children)
                    toast.show({ variant: 'success', description: t('ui:copySuccess') })
                  }}
                >
                  <Copy className={'size-5 text-text-quaternary'} />
                </Pressable>
              )}
              {onPress && <ChevronRight className={'size-5 text-text-tertiary'} />}
              {onOpenQrCode && <QrCode className={'size-5 text-text-secondary'} />}
            </View>
          </ListItemView>
        </View>
      )}
    </Pressable>
  )
}

interface ListItemViewProps extends ViewProps {
  hasBorderTop?: boolean
  hasBorderBottom?: boolean
  inset?: boolean
}

function ListItemView({ hasBorderTop = false, hasBorderBottom = false, inset = false, children }: ListItemViewProps) {
  return (
    <View
      className={cn(
        'min-h-[48px] flex-row justify-between border-border-10 pr-4',
        hasBorderTop && 'border-t',
        hasBorderTop ? 'pt-[13px]' : 'pt-[14px]',
        hasBorderBottom && 'border-b',
        hasBorderBottom ? 'pb-[13px]' : 'pb-[14px]',
        inset ? 'ml-4' : 'pl-4'
      )}
    >
      {children}
    </View>
  )
}

export default DescriptionListItem
